import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface ModuleFormProps {
  validation: any;
  applications?: any[];
  isEdit?: boolean;
  selectedApplicationId?: number | null;
}

const ModuleForm: React.FC<ModuleFormProps> = ({
  validation,
  applications = [],
  isEdit = false,
  selectedApplicationId = null,
}) => {
  // Filter to show only active applications
  const activeApplications = React.useMemo(() => {
    if (!Array.isArray(applications)) return [];
    return applications.filter((app: any) => 
      app.status?.toLowerCase() === "active"
    );
  }, [applications]);

  const applicationOptions = activeApplications.map((app) => ({
    label: app.applicationname,
    value: app.applicationid,
  }));

  return (
    <Row className="g-3">
      {!isEdit && (
        <Col md={12}>
          <Label className="fw-bold mb-1">
            Module ID <span className="text-danger">*</span>
          </Label>
          <Input
            type="number"
            name="moduleid"
            placeholder="Enter module ID"
            value={validation.values.moduleid || ""}
            onChange={(e) => {
              let value = e.target.value;
              value = value.replace(/[^0-9]/g, "");
              if (value.length > 4) {
                value = value.substring(0, 4);
              }
              validation.setFieldValue("moduleid", value ? Number(value) : "");
            }}
            onBlur={validation.handleBlur}
            max={9999}
            invalid={
              !!(validation.touched.moduleid && validation.errors.moduleid)
            }
          />
          <FormFeedback>{validation.errors.moduleid}</FormFeedback>
        </Col>
      )}

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Application <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={applicationOptions}
            name="applicationid"
            placeholder="Select Application"
            value={applicationOptions.find((opt) => {
              const formValue = validation.values.applicationid;
              const selectedValue = selectedApplicationId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => validation.setFieldValue("applicationid", e?.value)}
            onBlur={() => validation.setFieldTouched("applicationid", true)}
            classNamePrefix="react-select"
            className={validation.touched.applicationid && validation.errors.applicationid ? "react-select-error" : ""}
            isDisabled={true}
          />
        </div>
        {validation.touched.applicationid && validation.errors.applicationid && (
          <div className="invalid-feedback d-block">{validation.errors.applicationid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Module Name <span className="text-danger">*</span>
        </Label>
        <div className="position-relative">
          <Input
            type="text"
            name="modulename"
            placeholder="Enter module name"
            value={validation.values.modulename || ""}
            onChange={(e) => {
              let text = e.target.value;
              // Only allow alphabets and spaces
              text = text.replace(/[^a-zA-Z ]/g, "");
              // Limit to 70 characters
              if (text.length > 70) {
                text = text.substring(0, 70);
              }
              validation.setFieldValue("modulename", text);
            }}
            onBlur={validation.handleBlur}
            style={{ paddingRight: "50px" }}
            invalid={
              !!(validation.touched.modulename && validation.errors.modulename)
            }
          />
          <FormFeedback>{validation.errors.modulename}</FormFeedback>
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "12px",
              transform: "translateY(-50%)",
              fontSize: "12px",
              color: "#6c757d",
              pointerEvents: "none",
            }}
          >
            {(validation.values.modulename || "").length}/70
          </div>
        </div>
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Description <span className="text-danger">*</span>
        </Label>
        <div className="position-relative">
          <Input
            type="text"
            name="description"
            placeholder="Enter description"
            value={validation.values.description || ""}
            onChange={(e) => {
              let text = e.target.value;
              // Only allow alphabets and spaces
              text = text.replace(/[^a-zA-Z ]/g, "");
              // Limit to 100 characters
              if (text.length > 100) {
                text = text.substring(0, 100);
              }
              validation.setFieldValue("description", text);
            }}
            onBlur={validation.handleBlur}
            style={{ paddingRight: "60px" }}
            invalid={
              !!(validation.touched.description && validation.errors.description)
            }
          />
          <FormFeedback>{validation.errors.description}</FormFeedback>
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "12px",
              transform: "translateY(-50%)",
              fontSize: "12px",
              color: "#6c757d",
              pointerEvents: "none",
            }}
          >
            {(validation.values.description || "").length}/100
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default ModuleForm;
